import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { fetchTraderConversations, fetchConversationMessages, isFromTrader } from '../../lib/consoleConversations';
import { fetchFactoryClaimed } from '../../lib/traderCardData';
import TraderCardModal from '../../components/admin2/TraderCardModal';

// Every trader conversation — with a catalog factory or a registered supplier —
// in one read-only list (owner, 2026-10-11). The email alert fires once per
// pair; this page is where the team follows the rest. Deliberately no reply
// box: the console reads private chats, it never writes into them.

const FILTERS = [
  { key: 'all', ar: 'الكل', en: 'All' },
  { key: 'factory', ar: 'مع مصانع', en: 'With factories' },
  { key: 'supplier', ar: 'مع موردين مسجّلين', en: 'With suppliers' },
];

const when = (iso, isAr) => (iso ? new Date(iso).toLocaleString(isAr ? 'ar-SA' : 'en-GB', {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
}) : '');

export default function ConsoleConversations({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [thread, setThread] = useState(null);
  // The trader card is for factories that have NOT claimed their page yet
  // (handoff of 2026-10-17, item 1): null = unknown, so no button.
  const [factoryClaimed, setFactoryClaimed] = useState(null);
  const [cardOpen, setCardOpen] = useState(false);

  const sel = {
    channel: params.get('channel') || '',
    trader: params.get('trader') || '',
    counterpart: params.get('counterpart') || '',
  };
  const selKey = `${sel.channel}:${sel.trader}:${sel.counterpart}`;

  const load = () => {
    setRows(null); setError('');
    fetchTraderConversations().then(setRows).catch((e) => { setRows([]); setError(e.message); });
  };
  useEffect(load, []);

  useEffect(() => {
    if (!sel.trader || !sel.counterpart) { setThread(null); return undefined; }
    let alive = true;
    setThread(null);
    fetchConversationMessages(sel)
      .then((m) => { if (alive) setThread(m); })
      .catch((e) => { if (alive) { setThread([]); setError(e.message); } });
    return () => { alive = false; };
  }, [selKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setFactoryClaimed(null); setCardOpen(false);
    if (sel.channel !== 'factory' || !sel.counterpart) return undefined;
    let alive = true;
    fetchFactoryClaimed(sel.counterpart)
      .then((c) => { if (alive) setFactoryClaimed(c); })
      .catch((e) => { console.error('[ConsoleConversations] claim state failed:', e?.message || e); });
    return () => { alive = false; };
  }, [selKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rows || []).filter((r) => (filter === 'all' || r.channel === filter)
      && (!q || `${r.trader_name} ${r.counterpart_name} ${r.last_preview}`.toLowerCase().includes(q)));
  }, [rows, filter, search]);

  const open = (r) => setParams({ channel: r.channel, trader: r.trader_id, counterpart: r.counterpart_id });
  const current = (rows || []).find((r) => r.channel === sel.channel && r.trader_id === sel.trader && r.counterpart_id === sel.counterpart);
  // Only pages the new console has. A registered supplier has no /admin2 page
  // yet, and the owner's rule is never to land in the old console (2026-10-11)
  // — so that button appears when its page is built.
  const counterpartLink = current && current.channel === 'factory'
    ? `/admin2/suppliers/${current.counterpart_id}?tab=messages` : null;

  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="conversations">
      <div className="ac-page" style={{ fontFamily: font }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{isAr ? 'محادثات التجار' : 'Trader Conversations'}</h1>
            <p className="ac-sub">{isAr
              ? 'كل محادثة بين تاجر ومصنع أو مورد. للقراءة فقط — لا يكتب الفريق داخل المحادثات.'
              : 'Every conversation between a trader and a factory or supplier. Read-only — the team never writes inside a chat.'}</p>
          </div>
          <button className="ac-btn ac-btn-sm" onClick={load}>{isAr ? 'تحديث' : 'Refresh'}</button>
        </div>

        <div className="ac-chiprow" style={{ marginBottom: 12 }}>
          {FILTERS.map((f) => (
            <button key={f.key} className={`ac-chip${filter === f.key ? ' on' : ''}`} onClick={() => setFilter(f.key)}>
              {isAr ? f.ar : f.en}
            </button>
          ))}
          <input className="ac-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={isAr ? 'ابحث باسم التاجر أو المصنع…' : 'Search trader or factory…'}
            style={{ maxWidth: 260, height: 32, fontFamily: font }} />
        </div>

        {error && <div className="ac-placeholder" style={{ color: 'var(--ac-danger)', marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 320px', minWidth: 0, maxWidth: 460 }}>
            {rows == null ? (
              <div className="ac-skel" style={{ height: 90 }} />
            ) : shown.length === 0 ? (
              <div className="ac-placeholder">{isAr ? 'لا توجد محادثات.' : 'No conversations.'}</div>
            ) : shown.map((r) => {
              const on = r.channel === sel.channel && r.trader_id === sel.trader && r.counterpart_id === sel.counterpart;
              return (
                <button key={`${r.channel}:${r.trader_id}:${r.counterpart_id}`} className="ac-card" onClick={() => open(r)}
                  style={{ display: 'block', width: '100%', padding: 12, marginBottom: 8, cursor: 'pointer', fontFamily: 'inherit',
                    textAlign: isAr ? 'right' : 'left', border: `1px solid ${on ? 'var(--ac-ink)' : 'var(--ac-line)'}` }}>
                  <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13.5, fontWeight: 650, color: 'var(--ac-ink)' }}>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.trader_name || (isAr ? 'تاجر' : 'Trader')} ↔ {r.counterpart_name || '—'}
                    </span>
                    <span style={{ fontSize: 11.5, fontWeight: 400, color: 'var(--ac-faint)', flexShrink: 0 }}>{when(r.last_at, isAr)}</span>
                  </span>
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                    <span className={`ac-badge ${r.channel === 'factory' ? 'warn' : 'ok'}`}>
                      {r.channel === 'factory' ? (isAr ? 'مصنع' : 'Factory') : (isAr ? 'مورد' : 'Supplier')}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--ac-faint)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.last_from_trader ? (isAr ? 'التاجر: ' : 'Trader: ') : ''}{r.last_preview}
                    </span>
                    <span style={{ fontSize: 11.5, color: 'var(--ac-faint)', flexShrink: 0, marginInlineStart: 'auto' }}>{r.message_count}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="ac-card" style={{ flex: '2 1 360px', minWidth: 0, padding: 16, border: '1px solid var(--ac-line)' }}>
            {!sel.trader ? (
              <div className="ac-placeholder">{isAr ? 'اختر محادثة لعرضها.' : 'Pick a conversation to read it.'}</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 14, fontWeight: 650, color: 'var(--ac-ink)' }}>
                    {current ? `${current.trader_name || ''} ↔ ${current.counterpart_name || ''}` : (isAr ? 'محادثة' : 'Conversation')}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="ac-btn ac-btn-sm" onClick={() => nav(`/admin2/traders/${sel.trader}`)}>{isAr ? 'ملف التاجر' : 'Trader'}</button>
                    {counterpartLink && <button className="ac-btn ac-btn-sm" onClick={() => nav(counterpartLink)}>{isAr ? 'ملف المصنع' : 'Factory'}</button>}
                    {sel.channel === 'factory' && factoryClaimed === false && (
                      <button className="ac-btn ac-btn-sm ac-btn-primary" onClick={() => setCardOpen(true)}>{isAr ? 'بطاقة تاجر' : 'Trader card'}</button>
                    )}
                  </div>
                </div>
                {thread == null ? (
                  <div className="ac-skel" style={{ height: 120 }} />
                ) : thread.length === 0 ? (
                  <div className="ac-placeholder">{isAr ? 'لا رسائل.' : 'No messages.'}</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '65vh', overflowY: 'auto' }}>
                    {thread.map((m) => {
                      const mine = isFromTrader(m, sel);
                      return (
                        <div key={m.id} style={{ alignSelf: mine ? 'flex-start' : 'flex-end', maxWidth: '80%',
                          background: mine ? 'var(--ac-hover)' : 'var(--ac-panel)', border: '1px solid var(--ac-line)',
                          borderRadius: 10, padding: '8px 11px' }}>
                          <div style={{ fontSize: 11, color: 'var(--ac-faint)', marginBottom: 3 }}>
                            {mine ? (isAr ? 'التاجر' : 'Trader') : (sel.channel === 'factory' ? (isAr ? 'المصنع' : 'Factory') : (isAr ? 'المورد' : 'Supplier'))}
                            {' · '}{when(m.created_at, isAr)}
                          </div>
                          {m.content && <div dir="auto" style={{ fontSize: 13, color: 'var(--ac-ink)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.content}</div>}
                          {(m.file_name || m.file_url) && <div style={{ fontSize: 12, color: 'var(--ac-muted)', marginTop: 4 }}>📎 {m.file_name || (isAr ? 'مرفق' : 'Attachment')}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {cardOpen && sel.channel === 'factory' && (
        <TraderCardModal factoryId={sel.counterpart} trader={sel.trader} isAr={isAr} onClose={() => setCardOpen(false)} />
      )}
    </ConsoleShell>
  );
}
